import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Printer, ArrowLeft, Send, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../services/api';

const fmt = (n, c) => {
  try { return Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ' + (c || 'USD').toUpperCase(); }
  catch { return `${n} ${c || 'USD'}`; }
};

const Invoice = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/orders/${orderId}`);
        if (cancelled) return;
        setOrder(data);
        if (data?.vendor_id) {
          try {
            const v = await api.get(`/vendors/${data.vendor_id}`);
            if (!cancelled) setVendor(v.data);
          } catch { /* vendor may be hidden / pending — fall back to order.vendor_name */ }
        }
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || 'Could not load invoice');
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [orderId]);

  if (loading) return <div className="p-10 text-center text-slate-500">Loading invoice…</div>;
  if (err) return (
    <div className="p-10 max-w-md mx-auto text-center">
      <AlertTriangle className="h-10 w-10 mx-auto text-rose-500" />
      <p className="mt-3 text-slate-600">{err}</p>
      <Link to="/orders" className="text-emerald-700 underline mt-3 inline-block">Back to my orders</Link>
    </div>
  );

  const currency = order.currency || 'USD';
  const issued = order.placed_at ? new Date(order.placed_at) : new Date();
  const subtotal = order.items?.reduce((s, i) => s + (Number(i.price) || 0) * (i.qty || 1), 0) || 0;
  const delivery = Number(order.delivery_fee || 0);
  const service = Number(order.service_fee || 0);
  const tax = Number(order.tax_local || order.tax || 0);
  const discount = Number(order.promo_discount_local || order.promo_discount || 0);
  const total = Number(order.total || order.total_local || (subtotal + delivery + service + tax - discount));

  return (
    <div className="min-h-screen bg-slate-50 print:bg-white">
      {/* Toolbar — hidden on print */}
      <div className="print:hidden bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/orders" className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900" data-testid="invoice-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to orders
          </Link>
          <div className="flex items-center gap-2">
            <Button onClick={() => window.print()} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="invoice-print-btn">
              <Printer className="h-4 w-4 mr-1.5" /> Print invoice
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8 print:py-0 print:px-0">
        <div className="bg-white border border-slate-200 rounded-2xl p-8 sm:p-10 print:border-0 print:shadow-none print:rounded-none">
          {/* Header */}
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-semibold">ShipitNow invoice</div>
              <h1 className="text-3xl font-semibold text-slate-900 mt-1.5" data-testid="invoice-order-id">{order.order_id || order.id}</h1>
              <div className="text-sm text-slate-500 mt-1">Issued {issued.toLocaleDateString()} · {issued.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
            </div>
            <div className="text-right">
              <div className="inline-flex items-center gap-1.5 text-emerald-700 text-sm font-medium">
                <CheckCircle2 className="h-4 w-4" /> Paid
              </div>
              <div className="text-3xl font-semibold mt-1.5 text-slate-900" data-testid="invoice-total">{fmt(total, currency)}</div>
            </div>
          </div>

          {/* Parties */}
          <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="rounded-xl bg-slate-50 border border-slate-100 p-4">
              <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">From</div>
              <div className="font-semibold text-slate-900 mt-1">{vendor?.name || order.vendor_name || 'Vendor'}</div>
              {vendor?.address && <div className="text-sm text-slate-600 mt-0.5">{vendor.address}</div>}
              {vendor?.country && <div className="text-sm text-slate-600">{[vendor.city, vendor.state, vendor.country].filter(Boolean).join(', ')}</div>}
            </div>
            <div className="rounded-xl bg-slate-50 border border-slate-100 p-4">
              <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">Bill to</div>
              <div className="font-semibold text-slate-900 mt-1" data-testid="invoice-customer-name">{order.customer_name || 'Customer'}</div>
              {order.customer_email && <div className="text-sm text-slate-600 mt-0.5">{order.customer_email}</div>}
              {order.delivery_address && <div className="text-sm text-slate-600">{order.delivery_address}</div>}
            </div>
          </div>

          {/* Line items */}
          <table className="w-full mt-7 text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-200">
                <th className="py-2 font-normal">Item</th>
                <th className="py-2 text-center font-normal w-16">Qty</th>
                <th className="py-2 text-right font-normal w-28">Unit</th>
                <th className="py-2 text-right font-normal w-32">Amount</th>
              </tr>
            </thead>
            <tbody data-testid="invoice-line-items">
              {(order.items || []).map((it, idx) => (
                <tr key={idx} className="border-b border-slate-100">
                  <td className="py-3 text-slate-900">{it.name}</td>
                  <td className="py-3 text-center text-slate-600">{it.qty || 1}</td>
                  <td className="py-3 text-right text-slate-600">{fmt(it.price, currency)}</td>
                  <td className="py-3 text-right text-slate-900 font-medium">{fmt((it.price || 0) * (it.qty || 1), currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <div className="mt-5 ml-auto max-w-xs space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span>{fmt(subtotal, currency)}</span></div>
            {delivery > 0 && <div className="flex justify-between"><span className="text-slate-600">Delivery</span><span>{fmt(delivery, currency)}</span></div>}
            {service > 0 && <div className="flex justify-between"><span className="text-slate-600">Service fee</span><span>{fmt(service, currency)}</span></div>}
            {tax > 0 && <div className="flex justify-between"><span className="text-slate-600">Tax</span><span>{fmt(tax, currency)}</span></div>}
            {discount > 0 && <div className="flex justify-between text-emerald-700"><span>Discount {order.promo_code ? `(${order.promo_code})` : ''}</span><span>− {fmt(discount, currency)}</span></div>}
            <div className="flex justify-between border-t border-slate-200 pt-2.5 mt-2 font-semibold text-slate-900 text-base">
              <span>Total paid</span><span>{fmt(total, currency)}</span>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-10 pt-5 border-t border-slate-100 text-xs text-slate-500 flex items-center justify-between flex-wrap gap-2">
            <div>Stripe payment <code className="text-slate-700">{order.payment_intent_id || order.stripe_session_id || '—'}</code></div>
            <div className="flex items-center gap-1.5"><Send className="h-3.5 w-3.5" /> A copy was emailed to you, the vendor, and our platform admin.</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Invoice;
